import { useState, useEffect } from 'react';
import { Link, useNavigate } from 'react-router-dom';
import { getLeads, deleteLead, getUsers, importLeadsCSV, getReportUrl, getLeadsSampleCsvUrl } from '../../lib/api';
import api from '../../lib/api';
import { useAuth } from '../../context/AuthContext';
import { formatDate, formatPhone, getStatusBadgeClass, formatApiError } from '../../lib/utils';
import { LEAD_STATUSES, CLOSE_REASONS, buildUserOptions } from '../../lib/constants';
import { Button } from '../../components/ui/button';
import { Input } from '../../components/ui/input';
import { Label } from '../../components/ui/label';
import { Badge } from '../../components/ui/badge';
import { SearchableSelect } from '../../components/ui/searchable-select';
import { Skeleton } from '../../components/ui/skeleton';
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogDescription, DialogFooter } from '../../components/ui/dialog';
import {
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableHeader,
  TableRow,
} from '../../components/ui/table';
import {
  DropdownMenu,
  DropdownMenuContent,
  DropdownMenuItem,
  DropdownMenuSeparator,
  DropdownMenuSub,
  DropdownMenuSubContent,
  DropdownMenuSubTrigger,
  DropdownMenuTrigger,
} from '../../components/ui/dropdown-menu';
import {
  AlertDialog,
  AlertDialogAction,
  AlertDialogCancel,
  AlertDialogContent,
  AlertDialogDescription,
  AlertDialogFooter,
  AlertDialogHeader,
  AlertDialogTitle,
} from '../../components/ui/alert-dialog';
import { Tooltip, TooltipContent, TooltipProvider, TooltipTrigger } from '../../components/ui/tooltip';
import { toast } from 'sonner';
import { Plus, MagnifyingGlass, DotsThree, Eye, Pencil, Trash, UserPlus, ArrowsClockwise, CaretRight, UploadSimple, DownloadSimple, ImageSquare, Cake } from '@phosphor-icons/react';
import LeadImageImportDialog from './LeadImageImportDialog';
import LeadStatusFollowUpDialog, { defaultFollowUpForStatus } from './LeadStatusFollowUpDialog';
import WhatsAppNudgeDialog, { WHATSAPP_NUDGE_REASONS } from './WhatsAppNudgeDialog';

export default function LeadsPage() {
  const [leads, setLeads] = useState([]);
  const [users, setUsers] = useState({});
  const [sources, setSources] = useState([]);
  const [loading, setLoading] = useState(true);
  const [search, setSearch] = useState('');
  const [statusFilter, setStatusFilter] = useState('all');
  const [tab, setTab] = useState('open'); // open | closed | converted
  const [imageImportOpen, setImageImportOpen] = useState(false);
  const [followUpDialog, setFollowUpDialog] = useState({ open: false, lead: null, status: '' });
  const [waNudge, setWaNudge] = useState({ open: false, lead: null, reason: '' });
  const [sourceFilter, setSourceFilter] = useState('all');
  const [amFilter, setAmFilter] = useState('all');
  const [usersList, setUsersList] = useState([]);
  const [deleteId, setDeleteId] = useState(null);
  const [statusUpdateLead, setStatusUpdateLead] = useState(null);
  const [newStatus, setNewStatus] = useState('');
  const [closeReason, setCloseReason] = useState('');
  const [closeReasonOther, setCloseReasonOther] = useState('');
  const [updatingStatus, setUpdatingStatus] = useState(false);
  const [importing, setImporting] = useState(false);
  const [importDialogOpen, setImportDialogOpen] = useState(false);
  const [importFile, setImportFile] = useState(null);
  const [importAssignTo, setImportAssignTo] = useState('');
  const { isAdmin } = useAuth();
  const navigate = useNavigate();

  const handleCSVImport = async () => {
    if (!importFile) {
      toast.error('Please choose a CSV file first');
      return;
    }
    setImporting(true);
    try {
      const res = await importLeadsCSV(importFile, importAssignTo || null);
      const { message, errors = [], total_errors = 0 } = res.data || {};
      if (total_errors > 0) {
        const preview = errors.slice(0, 3).join(' | ');
        toast.warning(`${message}. ${total_errors} error(s): ${preview}${total_errors > 3 ? ' …' : ''}`, { duration: 8000 });
        // Also log full error list for the user to copy
        console.warn('Bulk import errors:', errors);
      } else {
        toast.success(message);
      }
      setImportDialogOpen(false);
      setImportFile(null);
      setImportAssignTo('');
      fetchLeads();
    } catch (error) {
      toast.error(formatApiError(error));
    } finally {
      setImporting(false);
    }
  };

  const fetchLeads = async () => {
    try {
      const params = {};
      if (search) params.search = search;
      // Tab takes precedence over the optional Status dropdown
      if (tab === 'closed') params.status = 'Closed';
      else if (tab === 'converted') params.status = 'Converted';
      else if (statusFilter !== 'all') params.status = statusFilter;
      else params.status = 'open';
      if (sourceFilter !== 'all') params.source = sourceFilter;
      if (amFilter !== 'all') params.assigned_to = amFilter;
      
      const [leadsRes, usersRes, sourcesRes] = await Promise.all([
        getLeads(params),
        getUsers(),
        api.get('/settings/lead_sources').catch(() => ({ data: { items: [] } }))
      ]);
      setLeads(leadsRes.data);
      const usersMap = {};
      usersRes.data.forEach(u => { usersMap[u.id] = u.name; });
      setUsers(usersMap);
      setUsersList(usersRes.data);
      const activeItems = sourcesRes.data.items?.filter(i => i.is_active).map(i => i.value) || [];
      setSources(activeItems.length > 0 ? activeItems : ['Referral', 'Offline', 'Online', 'Instagram', 'LinkedIn']);
    } catch (error) {
      toast.error(formatApiError(error));
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    fetchLeads();
  }, [tab, statusFilter, sourceFilter, amFilter]); // eslint-disable-line react-hooks/exhaustive-deps

  const handleSearch = (e) => {
    e.preventDefault();
    fetchLeads();
  };

  // Birthday detection: returns 'today' | 'week' | null based on dob YYYY-MM-DD
  const getBirthdayBadge = (dob) => {
    if (!dob) return null;
    const m = String(dob).match(/^(\d{4})-(\d{2})-(\d{2})/);
    if (!m) return null;
    const month = parseInt(m[2], 10);
    const day = parseInt(m[3], 10);
    const today = new Date();
    if (today.getMonth() + 1 === month && today.getDate() === day) return 'today';
    // upcoming within 7 days (this calendar year)
    const thisYear = today.getFullYear();
    const upcoming = new Date(thisYear, month - 1, day);
    if (upcoming < today) upcoming.setFullYear(thisYear + 1);
    const diffDays = Math.floor((upcoming - today) / (24 * 60 * 60 * 1000));
    if (diffDays >= 0 && diffDays <= 7) return 'week';
    return null;
  };

  const handleDelete = async () => {
    if (!deleteId) return;
    try {
      await deleteLead(deleteId);
      toast.success('Lead deleted successfully');
      setLeads(leads.filter(l => l.id !== deleteId));
    } catch (error) {
      toast.error(formatApiError(error));
    } finally {
      setDeleteId(null);
    }
  };

  const handleQuickStatusChange = async (leadId, status) => {
    if (status === 'Closed') {
      // Existing close-reason flow
      setStatusUpdateLead(leads.find(l => l.id === leadId));
      setNewStatus('Closed');
      return;
    }

    // Statuses that suggest a follow-up → open the unified dialog (user can skip).
    if (defaultFollowUpForStatus(status)) {
      const lead = leads.find(l => l.id === leadId);
      if (lead) {
        setFollowUpDialog({ open: true, lead, status });
        return;
      }
    }

    // No follow-up suggestion → patch immediately.
    try {
      await api.patch(`/leads/${leadId}/status`, { status });
      setLeads(leads.map(l => l.id === leadId ? { ...l, status } : l));
      toast.success(`Lead status updated to ${status}`);
    } catch (error) {
      toast.error(formatApiError(error));
    }
  };

  const handleStatusUpdate = async () => {
    if (!statusUpdateLead) return;
    setUpdatingStatus(true);
    try {
      const payload = {
        status: newStatus,
        close_reason: newStatus === 'Closed' ? closeReason : null,
        close_reason_other: closeReason === 'Other' ? closeReasonOther : null
      };
      await api.patch(`/leads/${statusUpdateLead.id}/status`, payload);
      const closedLead = statusUpdateLead;
      const reasonChosen = closeReason;
      setLeads(leads.map(l => l.id === closedLead.id ? { ...l, status: newStatus, close_reason: closeReason, close_reason_other: closeReasonOther } : l));
      toast.success(`Lead status updated to ${newStatus}`);
      setStatusUpdateLead(null);
      setNewStatus('');
      setCloseReason('');
      setCloseReasonOther('');
      // Offer the WhatsApp nudge for soft-closure reasons
      if (newStatus === 'Closed' && WHATSAPP_NUDGE_REASONS.has(reasonChosen) && closedLead.phone) {
        setWaNudge({ open: true, lead: closedLead, reason: reasonChosen });
      }
    } catch (error) {
      toast.error(formatApiError(error));
    } finally {
      setUpdatingStatus(false);
    }
  };

  return (
    <div className="p-6 space-y-6" data-testid="leads-page">
      {/* Header */}
      <div className="flex items-center justify-between">
        <div>
          <h1 className="text-2xl font-bold tracking-tight" style={{ fontFamily: 'Manrope' }}>
            Leads
          </h1>
          <p className="text-sm text-zinc-500 mt-1">
            Manage your prospective clients
          </p>
        </div>
        <div className="flex gap-2">
          <DropdownMenu>
            <DropdownMenuTrigger asChild>
              <Button variant="outline" className="rounded-sm" data-testid="export-leads-btn">
                <DownloadSimple size={18} className="mr-2" /> Export
              </Button>
            </DropdownMenuTrigger>
            <DropdownMenuContent align="end">
              <DropdownMenuItem onClick={() => window.open(getReportUrl('leads', 'csv'), '_blank')}>
                Export as CSV
              </DropdownMenuItem>
              <DropdownMenuItem onClick={() => window.open(getReportUrl('leads', 'excel'), '_blank')}>
                Export as Excel
              </DropdownMenuItem>
            </DropdownMenuContent>
          </DropdownMenu>
          <Button variant="outline" className="rounded-sm" onClick={() => setImportDialogOpen(true)} data-testid="import-csv-btn">
            <UploadSimple size={18} className="mr-2" /> Import CSV
          </Button>
          <Button variant="outline" className="rounded-sm" onClick={() => setImageImportOpen(true)} data-testid="import-image-btn">
            <ImageSquare size={18} className="mr-2" /> Import from Image
          </Button>
          <Button
            onClick={() => navigate('/leads/new')}
            className="bg-[#002FA7] hover:bg-[#00227A] text-white rounded-sm"
            data-testid="create-lead-button"
          >
            <Plus size={18} className="mr-2" />
            Add Lead
          </Button>
        </div>
      </div>

      {/* Tabs: Open / Closed / Converted */}
      <div className="flex border-b border-zinc-200 -mt-2">
        {[
          { key: 'open', label: 'Open' },
          { key: 'closed', label: 'Closed' },
          { key: 'converted', label: 'Converted' },
        ].map((t) => {
          const active = tab === t.key;
          return (
            <button
              key={t.key}
              onClick={() => { setTab(t.key); setStatusFilter('all'); }}
              className={`px-4 py-2 text-sm font-medium tracking-wide border-b-2 -mb-px transition-colors ${active ? 'border-[#002FA7] text-[#002FA7]' : 'border-transparent text-zinc-500 hover:text-zinc-800'}`}
              data-testid={`leads-tab-${t.key}`}
            >
              {t.label}
            </button>
          );
        })}
      </div>

      {/* Filters */}
      <div className="space-y-3">
        <form onSubmit={handleSearch}>
          <div className="relative">
            <MagnifyingGlass size={18} className="absolute left-3 top-1/2 -translate-y-1/2 text-zinc-400" />
            <Input
              placeholder="Search by name, phone, or email..."
              value={search}
              onChange={(e) => setSearch(e.target.value)}
              className="pl-10 rounded-sm"
              data-testid="leads-search-input"
            />
          </div>
        </form>
        <div className="flex gap-2 overflow-x-auto pb-1 filter-bar">
        <SearchableSelect
          value={statusFilter}
          onValueChange={setStatusFilter}
          options={[{ value: 'all', label: 'All Statuses' }, ...LEAD_STATUSES.map(s => ({ value: s, label: s }))]}
          placeholder="Status"
          triggerClassName="w-[160px]"
          data-testid="status-filter"
        />
        <SearchableSelect
          value={sourceFilter}
          onValueChange={setSourceFilter}
          options={[{ value: 'all', label: 'All Sources' }, ...sources.map(s => ({ value: s, label: s }))]}
          placeholder="Source"
          triggerClassName="w-[160px]"
          data-testid="source-filter"
        />
        <SearchableSelect
          value={amFilter}
          onValueChange={setAmFilter}
          options={[{ value: 'all', label: 'All Members' }, ...buildUserOptions(usersList.filter(u => u.status === 'active' || u.id))]}
          placeholder="Team (AM / SAM / OM)"
          searchPlaceholder="Search..."
          triggerClassName="w-[180px]"
          data-testid="am-filter"
        />
        </div>
      </div>

      {/* Table */}
      <div className="border border-zinc-200 rounded-sm overflow-hidden bg-white">
        {loading ? (
          <div className="p-6 space-y-4">
            {[1, 2, 3, 4, 5].map(i => <Skeleton key={i} className="h-12" />)}
          </div>
        ) : leads.length === 0 ? (
          <div className="p-12 text-center">
            <UserPlus size={48} className="mx-auto text-zinc-300 mb-4" />
            <p className="text-zinc-500">No leads found</p>
            <Button
              onClick={() => navigate('/leads/new')}
              variant="link"
              className="text-[#002FA7] mt-2"
            >
              Create your first lead
            </Button>
          </div>
        ) : (
          <div className="overflow-x-auto">
          <Table>
            <TableHeader>
              <TableRow className="bg-zinc-50">
                <TableHead className="text-xs uppercase tracking-[0.1em] font-semibold">Name</TableHead>
                <TableHead className="text-xs uppercase tracking-[0.1em] font-semibold">Phone</TableHead>
                <TableHead className="text-xs uppercase tracking-[0.1em] font-semibold">Source</TableHead>
                <TableHead className="text-xs uppercase tracking-[0.1em] font-semibold">Status</TableHead>
                <TableHead className="text-xs uppercase tracking-[0.1em] font-semibold">Assigned To</TableHead>
                <TableHead className="text-xs uppercase tracking-[0.1em] font-semibold">Last Activity</TableHead>
                <TableHead className="text-xs uppercase tracking-[0.1em] font-semibold">Next Follow-up</TableHead>
                <TableHead className="text-xs uppercase tracking-[0.1em] font-semibold">Created</TableHead>
                <TableHead className="w-[50px]"></TableHead>
              </TableRow>
            </TableHeader>
            <TableBody>
              {leads.map((lead) => (
                <TableRow key={lead.id} className="hover:bg-zinc-50" data-testid={`lead-row-${lead.id}`}>
                  <TableCell className="font-medium">
                    <Link to={`/leads/${lead.id}`} className="hover:text-[#002FA7]">
                      {lead.company_name || lead.full_name || (lead.member_id ? `Gymkhana #${lead.member_id}` : '—')}
                    </Link>
                    <div className="flex items-center gap-1.5 mt-0.5 flex-wrap">
                      {lead.company_name && lead.full_name && (
                        <p className="text-xs text-zinc-500">{lead.full_name}</p>
                      )}
                      {(() => {
                        const bd = getBirthdayBadge(lead.dob);
                        if (!bd) return null;
                        const cls = bd === 'today' ? 'bg-pink-100 text-pink-700 border-pink-200' : 'bg-amber-100 text-amber-700 border-amber-200';
                        const label = bd === 'today' ? 'Birthday today' : 'Birthday this week';
                        return (
                          <span className={`inline-flex items-center gap-1 text-[10px] uppercase tracking-wider px-1.5 py-0.5 rounded-sm border ${cls}`} data-testid={`birthday-badge-${lead.id}`}>
                            <Cake size={10} weight="fill" /> {label}
                          </span>
                        );
                      })()}
                      {lead.member_id && (
                        <span className="text-[10px] uppercase tracking-wider text-zinc-500 bg-zinc-100 border border-zinc-200 px-1.5 py-0.5 rounded-sm">
                          ID {lead.member_id}
                        </span>
                      )}
                    </div>
                  </TableCell>
                  <TableCell className="tabular-nums">{formatPhone(lead.phone)}</TableCell>
                  <TableCell>{lead.source}</TableCell>
                  <TableCell>
                    <DropdownMenu>
                      <DropdownMenuTrigger asChild>
                        <button className="cursor-pointer" data-testid={`status-trigger-${lead.id}`}>
                          {lead.status === 'Closed' && lead.close_reason ? (
                            <TooltipProvider delayDuration={150}>
                              <Tooltip>
                                <TooltipTrigger asChild>
                                  <Badge className={`${getStatusBadgeClass(lead.status)} text-xs hover:opacity-80 transition-opacity`}>
                                    {lead.status}
                                  </Badge>
                                </TooltipTrigger>
                                <TooltipContent side="top" className="max-w-xs bg-zinc-900 text-white text-xs">
                                  <p className="font-semibold mb-0.5">Closure reason</p>
                                  <p data-testid={`close-reason-tooltip-${lead.id}`}>
                                    {lead.close_reason === 'Other' && lead.close_reason_other
                                      ? `Other — ${lead.close_reason_other}`
                                      : lead.close_reason}
                                  </p>
                                </TooltipContent>
                              </Tooltip>
                            </TooltipProvider>
                          ) : (
                            <Badge className={`${getStatusBadgeClass(lead.status)} text-xs hover:opacity-80 transition-opacity`}>
                              {lead.status}
                            </Badge>
                          )}
                        </button>
                      </DropdownMenuTrigger>
                      <DropdownMenuContent align="start">
                        <div className="px-2 py-1.5 text-xs font-semibold text-zinc-500 uppercase tracking-wider">
                          Change Status
                        </div>
                        {LEAD_STATUSES.filter(s => s !== lead.status).map(status => (
                          <DropdownMenuItem 
                            key={status} 
                            onClick={() => handleQuickStatusChange(lead.id, status)}
                            data-testid={`status-option-${status}`}
                          >
                            <Badge className={`${getStatusBadgeClass(status)} text-xs mr-2`}>{status}</Badge>
                          </DropdownMenuItem>
                        ))}
                      </DropdownMenuContent>
                    </DropdownMenu>
                  </TableCell>
                  <TableCell>{users[lead.assigned_to] || '-'}</TableCell>
                  <TableCell>
                    {lead.last_activity_at ? (
                      <span className="text-xs text-zinc-700" data-testid={`last-activity-${lead.id}`}>
                        {formatDate(lead.last_activity_at)}
                        {lead.last_activity_type && (
                          <span className="block text-[10px] uppercase tracking-wider text-zinc-400">{lead.last_activity_type}</span>
                        )}
                      </span>
                    ) : (
                      <span className="text-xs text-zinc-400">—</span>
                    )}
                  </TableCell>
                  <TableCell>
                    {lead.next_follow_up_date ? (
                      <span className={`text-xs font-medium ${new Date(lead.next_follow_up_date) < new Date() ? 'text-red-600' : 'text-green-600'}`} data-testid={`follow-up-date-${lead.id}`}>
                        {formatDate(lead.next_follow_up_date)}
                      </span>
                    ) : (
                      <span className="text-xs text-zinc-400">—</span>
                    )}
                  </TableCell>
                  <TableCell className="text-zinc-500">{formatDate(lead.created_at)}</TableCell>
                  <TableCell>
                    <DropdownMenu>
                      <DropdownMenuTrigger asChild>
                        <Button variant="ghost" size="sm" className="h-8 w-8 p-0">
                          <DotsThree size={18} />
                        </Button>
                      </DropdownMenuTrigger>
                      <DropdownMenuContent align="end">
                        <DropdownMenuItem onClick={() => navigate(`/leads/${lead.id}`)}>
                          <Eye size={16} className="mr-2" /> View
                        </DropdownMenuItem>
                        <DropdownMenuItem onClick={() => navigate(`/leads/${lead.id}/edit`)}>
                          <Pencil size={16} className="mr-2" /> Edit
                        </DropdownMenuItem>
                        <DropdownMenuSeparator />
                        <DropdownMenuSub>
                          <DropdownMenuSubTrigger>
                            <ArrowsClockwise size={16} className="mr-2" /> Change Status
                          </DropdownMenuSubTrigger>
                          <DropdownMenuSubContent>
                            {LEAD_STATUSES.map(status => (
                              <DropdownMenuItem 
                                key={status} 
                                onClick={() => handleQuickStatusChange(lead.id, status)}
                                disabled={status === lead.status}
                              >
                                <Badge className={`${getStatusBadgeClass(status)} text-xs mr-2`}>{status}</Badge>
                              </DropdownMenuItem>
                            ))}
                          </DropdownMenuSubContent>
                        </DropdownMenuSub>
                        {isAdmin && (
                          <>
                            <DropdownMenuSeparator />
                            <DropdownMenuItem 
                              onClick={() => setDeleteId(lead.id)}
                              className="text-red-600"
                            >
                              <Trash size={16} className="mr-2" /> Delete
                            </DropdownMenuItem>
                          </>
                        )}
                      </DropdownMenuContent>
                    </DropdownMenu>
                  </TableCell>
                </TableRow>
              ))}
            </TableBody>
          </Table>
          </div>
        )}
      </div>

      {/* Status Update Dialog (for Closed status with reason) */}
      <Dialog open={!!statusUpdateLead && newStatus === 'Closed'} onOpenChange={() => { setStatusUpdateLead(null); setNewStatus(''); }}>
        <DialogContent>
          <DialogHeader>
            <DialogTitle>Close Lead: {statusUpdateLead?.full_name}</DialogTitle>
          </DialogHeader>
          <div className="py-4 space-y-4">
            <div className="space-y-2">
              <Label className="text-xs uppercase font-semibold">Close Reason *</Label>
              <SearchableSelect
                value={closeReason}
                onValueChange={setCloseReason}
                options={CLOSE_REASONS}
                placeholder="Select reason"
                data-testid="close-reason-select"
              />
            </div>
            {closeReason === 'Other' && (
              <div className="space-y-2">
                <Label className="text-xs uppercase font-semibold">Specify Reason</Label>
                <Input
                  value={closeReasonOther}
                  onChange={(e) => setCloseReasonOther(e.target.value)}
                  placeholder="Enter reason"
                  className="rounded-sm"
                />
              </div>
            )}
            <div className="flex justify-end gap-3 pt-4">
              <Button variant="outline" onClick={() => { setStatusUpdateLead(null); setNewStatus(''); }}>
                Cancel
              </Button>
              <Button 
                onClick={handleStatusUpdate} 
                disabled={updatingStatus || !closeReason}
                className="bg-[#002FA7] text-white"
                data-testid="confirm-close-button"
              >
                {updatingStatus ? 'Updating...' : 'Close Lead'}
              </Button>
            </div>
          </div>
        </DialogContent>
      </Dialog>

      {/* Delete Dialog */}
      <AlertDialog open={!!deleteId} onOpenChange={() => setDeleteId(null)}>
        <AlertDialogContent>
          <AlertDialogHeader>
            <AlertDialogTitle>Delete Lead</AlertDialogTitle>
            <AlertDialogDescription>
              Are you sure you want to delete this lead? This action cannot be undone.
            </AlertDialogDescription>
          </AlertDialogHeader>
          <AlertDialogFooter>
            <AlertDialogCancel>Cancel</AlertDialogCancel>
            <AlertDialogAction
              onClick={handleDelete}
              className="bg-red-600 hover:bg-red-700"
            >
              Delete
            </AlertDialogAction>
          </AlertDialogFooter>
        </AlertDialogContent>
      </AlertDialog>

      {/* Bulk Import Dialog */}
      <Dialog open={importDialogOpen} onOpenChange={(open) => { if (!open) { setImportDialogOpen(false); setImportFile(null); setImportAssignTo(''); } }}>
        <DialogContent className="max-w-lg" data-testid="import-dialog">
          <DialogHeader>
            <DialogTitle>Bulk Import Leads</DialogTitle>
            <DialogDescription>
              Upload a CSV file. Need help? <a href={getLeadsSampleCsvUrl()} className="text-[#002FA7] underline" target="_blank" rel="noreferrer" data-testid="sample-csv-link">Download sample CSV</a>.
            </DialogDescription>
          </DialogHeader>
          <div className="space-y-4 py-2">
            <div className="space-y-2">
              <Label className="text-xs uppercase tracking-[0.1em] font-semibold">Choose CSV file</Label>
              <Input
                type="file"
                accept=".csv"
                onChange={(e) => setImportFile(e.target.files?.[0] || null)}
                className="rounded-sm"
                data-testid="csv-file-input"
              />
              {importFile && <p className="text-xs text-zinc-500">Selected: {importFile.name} ({Math.round(importFile.size / 1024)} KB)</p>}
            </div>

            <div className="space-y-2">
              <Label className="text-xs uppercase tracking-[0.1em] font-semibold">Assign all to (optional)</Label>
              <SearchableSelect
                value={importAssignTo}
                onValueChange={setImportAssignTo}
                options={[{ value: '', label: 'Use CSV row data (or assign to me)' }, ...buildUserOptions((usersList || []).filter(u => u.status === 'active' || u.id))]}
                placeholder="Select employee"
                searchPlaceholder="Search employees..."
                data-testid="import-assign-select"
              />
              <p className="text-xs text-zinc-400">Leave blank to use the CSV's <code>assigned_to_phone</code> column. When blank, every row MUST have a valid <code>assigned_to_phone</code>.</p>
            </div>

            <div className="bg-zinc-50 p-3 rounded-sm text-xs text-zinc-600 space-y-1.5">
              <p className="font-semibold text-zinc-700">Required columns: <code>phone</code>, <code>source</code>, <code>campaign_name</code>, <code>full_name</code></p>
              <p className="text-[11px] text-zinc-500">• <code>assigned_to_phone</code> is required unless &quot;Assign all to&quot; is selected above.</p>
              <p className="text-[11px] text-zinc-500">• <code>full_name</code> may be blank <strong>only</strong> when <code>source</code> is exactly &quot;Apna Jalandhar&quot;.</p>
              <p className="text-[11px] text-zinc-500">• Rows missing required fields will be skipped and listed in the response.</p>
              <code className="block whitespace-pre-wrap break-words text-[11px] mt-1.5">full_name, company_name, phone, secondary_phone, tertiary_phone, email, source, campaign_name, service_interest, address, notes, assigned_to_phone</code>
            </div>
          </div>
          <DialogFooter>
            <Button variant="outline" onClick={() => setImportDialogOpen(false)}>Cancel</Button>
            <Button onClick={handleCSVImport} disabled={!importFile || importing} className="bg-[#002FA7] text-white" data-testid="import-csv-submit">
              {importing ? 'Importing...' : 'Import Leads'}
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      {/* Image-based bulk import (Gymkhana directory etc.) */}
      <LeadImageImportDialog
        open={imageImportOpen}
        onOpenChange={setImageImportOpen}
        usersList={usersList}
        onImported={fetchLeads}
      />

      {/* Status change with optional follow-up suggestion */}
      <LeadStatusFollowUpDialog
        open={followUpDialog.open}
        onOpenChange={(v) => setFollowUpDialog((prev) => ({ ...prev, open: v }))}
        lead={followUpDialog.lead}
        newStatus={followUpDialog.status}
        onUpdated={({ status }) => {
          setLeads((prev) => prev.map((l) => (l.id === followUpDialog.lead?.id ? { ...l, status } : l)));
          // Refresh list so next_follow_up_date / last_activity_at update
          fetchLeads();
        }}
      />

      {/* WhatsApp nudge after closing with a soft-closure reason */}
      <WhatsAppNudgeDialog
        open={waNudge.open}
        onOpenChange={(v) => setWaNudge((prev) => ({ ...prev, open: v }))}
        leadId={waNudge.lead?.id}
        leadName={waNudge.lead?.full_name || waNudge.lead?.company_name}
        leadPhone={waNudge.lead?.phone}
        closeReason={waNudge.reason}
      />
    </div>
  );
}
